import { useMemo, useState } from "react";

/**
 * Client-side table sorting.
 *
 * Columns are declared as a map from a sort key to the value to sort on, in the
 * same declarative spirit as the leaderboard `Category` list. Nulls always sort
 * last regardless of direction — a manager with no recorded stat should not
 * top a "fewest interceptions" column by virtue of having never played.
 */
export type SortDir = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

export type SortValue = number | string | null | undefined;

export function useSortedRows<
  T,
  A extends Record<string, (row: T) => SortValue>,
>(rows: T[], accessors: A, initial: SortState<Extract<keyof A, string>>) {
  type K = Extract<keyof A, string>;
  const [sort, setSort] = useState<SortState<K>>(initial);

  /** Click the active column to flip direction, a new one to sort by it. */
  const onSort = (key: K) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === initial.key ? initial.dir : "desc" },
    );

  const sorted = useMemo(() => {
    const get = accessors[sort.key];
    if (!get) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      const aNull = av == null || av === "";
      const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv)) * factor;
      }
      return (Number(av) - Number(bv)) * factor;
    });
  }, [rows, accessors, sort]);

  return { sorted, sort, onSort };
}
